import React, { useState, useRef, useMemo } from 'react';
import { Modal, Form, Button, Row, Col, Alert, ProgressBar, Badge } from 'react-bootstrap';
import {
  UploadCloud,
  FileText,
  Film,
  Headphones,
  Image as ImageIcon,
  CheckCircle,
  X,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import { useJubileeAuth } from '../auth/JubileeAuthContext';

const DETECT_MEDIA_TYPE = (file) => {
  if (!file) return 'document';
  const mime = file.type?.toLowerCase() || '';
  const name = file.name?.toLowerCase() || '';

  if (mime.startsWith('video/') || name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi') || name.endsWith('.mkv')) {
    return 'video';
  }
  if (mime.startsWith('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.ogg') || name.endsWith('.m4a')) {
    return 'audio';
  }
  if (mime.startsWith('image/') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp') || name.endsWith('.gif')) {
    return 'image';
  }
  return 'document';
};

export default function JubileeUploadModal({ show, onHide, onUploadSuccess }) {
  const { currentUser } = useJubileeAuth();
  const fileInputRef = useRef(null);

  const categories = useMemo(() => jubileeStorageService.getCategories(), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);

  // Form State
  const [selectedFile, setSelectedFile] = useState(null);
  const [mediaType, setMediaType] = useState('document');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventId, setEventId] = useState(events[0]?.id || '');
  const [categoryId, setCategoryId] = useState(categories[0]?.id || '');
  const [year, setYear] = useState('2026');
  const [date, setDate] = useState('2026-01-15');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(['Jubilee', 'Archive']);
  const [person, setPerson] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [source, setSource] = useState('JEMPARC Province Archive Repository');
  const [featured, setFeatured] = useState(false);
  const [visibility, setVisibility] = useState('public');

  // Status State
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = (file) => {
    if (!file) return;

    // File validation: check size (max 250MB)
    if (file.size > 250 * 1024 * 1024) {
      setError('Selected file exceeds maximum allowed size of 250MB.');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Auto-detect media type
    const detected = DETECT_MEDIA_TYPE(file);
    setMediaType(detected);

    // Auto-populate title if empty
    if (!title) {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setTitle(cleanName.charAt(0).toUpperCase() + cleanName.slice(1));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleAddTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '');
      if (!tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const resetForm = () => {
    setSelectedFile(null);
    setTitle('');
    setDescription('');
    setTags(['Jubilee', 'Archive']);
    setTagInput('');
    setPerson('');
    setSpeaker('');
    setProgress(0);
    setError('');
    setSuccess(false);
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Please provide a descriptive title for this archival record.');
      return;
    }

    setError('');
    setUploading(true);
    setProgress(15);

    try {
      // Simulate progressive file upload chunking
      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressTimer);
            return 90;
          }
          return prev + 25;
        });
      }, 150);

      // Create simulated / real object URL for the uploaded file
      let fileUrl = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
      let thumbnailUrl = 'https://images.unsplash.com/photo-1548625361-195982885942?auto=format&fit=crop&w=800&q=80';

      if (selectedFile) {
        fileUrl = URL.createObjectURL(selectedFile);
        if (mediaType === 'image') {
          thumbnailUrl = fileUrl;
        } else if (mediaType === 'video') {
          thumbnailUrl = 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=800&q=80';
        } else if (mediaType === 'audio') {
          thumbnailUrl = 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=80';
        } else {
          thumbnailUrl = 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=600&q=80';
        }
      }

      const selectedEventObj = events.find((ev) => ev.id === eventId);
      const selectedCategoryObj = categories.find((c) => c.id === categoryId);

      const newItem = {
        title: title.trim(),
        description: description.trim(),
        mediaType,
        fileName: selectedFile ? selectedFile.name : `archival_upload_${Date.now()}.${mediaType === 'video' ? 'mp4' : mediaType === 'audio' ? 'mp3' : 'pdf'}`,
        fileUrl,
        thumbnailUrl,
        eventId,
        eventName: selectedEventObj?.title || 'General Jubilee Event',
        categoryId,
        categoryName: selectedCategoryObj?.name || 'General Collection',
        year: year || '2026',
        date: date || '2026-01-15',
        tags,
        person: person.trim(),
        speaker: speaker.trim() || person.trim(),
        author: person.trim() || currentUser?.name || 'Province Archivist',
        source: source.trim(),
        duration: mediaType === 'video' ? '08:45' : mediaType === 'audio' ? '06:30' : '',
        mimeType: selectedFile ? selectedFile.type : (mediaType === 'video' ? 'video/mp4' : mediaType === 'audio' ? 'audio/mp3' : 'application/pdf'),
        fileSize: selectedFile ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB` : '4.5 MB',
        visibility,
        featured,
        uploadedBy: currentUser?.name || 'Archivist Admin',
        pageCount: mediaType === 'document' ? 16 : undefined,
      };

      setTimeout(() => {
        clearInterval(progressTimer);
        setProgress(100);
        jubileeStorageService.addMediaItem(newItem);
        setSuccess(true);
        setUploading(false);

        if (onUploadSuccess) onUploadSuccess(newItem);

        setTimeout(() => {
          resetForm();
          onHide();
        }, 1200);
      }, 700);
    } catch (err) {
      setError(err.message || 'Failed to upload media item');
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="rounded-4 border-0 shadow-2xl overflow-hidden">
      <div className="bg-dark text-white p-4 position-relative border-bottom border-secondary border-opacity-25" style={{ background: 'linear-gradient(135deg, #092e20 0%, #1e293b 100%)' }}>
        <div className="d-flex align-items-center gap-2 mb-1">
          <div className="p-2 bg-success bg-opacity-25 rounded-circle text-success">
            <UploadCloud size={20} />
          </div>
          <Badge bg="success" className="fw-bold">
            Archival Ingestion Portal
          </Badge>
        </div>
        <h4 className="fw-bold m-0 text-white">Upload to MDU Platinum Jubilee Archive</h4>
        <p className="text-light text-opacity-75 small m-0 mt-1">
          Add historical videos, audio recordings, photographs, or official documents into the heritage repository.
        </p>
        <button
          className="btn btn-link text-white-50 hover-text-white position-absolute top-0 end-0 m-3 p-1"
          onClick={onHide}
        >
          <X size={20} />
        </button>
      </div>

      <div className="modal-body p-4 bg-white" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
        {success && (
          <Alert variant="success" className="py-2 small d-flex align-items-center gap-2">
            <CheckCircle size={18} /> Media successfully catalogued and saved to the Platinum Jubilee archive!
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* 1. Drag & Drop File Ingestion Zone */}
          <div
            className={`upload-dropzone mb-4 ${isDragging ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="d-none"
              onChange={(e) => e.target.files && handleFileChange(e.target.files[0])}
            />

            <div className="d-flex flex-column align-items-center">
              <div className="p-3 bg-success bg-opacity-10 text-success rounded-circle mb-2">
                <UploadCloud size={32} />
              </div>
              {selectedFile ? (
                <div>
                  <h6 className="fw-bold text-dark m-0">{selectedFile.name}</h6>
                  <p className="text-muted small m-0">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • Detected: <strong className="text-uppercase text-success">{mediaType}</strong>
                  </p>
                  <span className="badge bg-light text-secondary border mt-2">Click to replace file</span>
                </div>
              ) : (
                <div>
                  <h6 className="fw-bold text-dark mb-1">Drag & drop archival files here, or click to browse</h6>
                  <p className="text-muted small m-0">
                    Supported: MP4 / WebM (Video), MP3 / WAV (Audio), JPG / PNG (Photo), PDF / DOCX (Documents) up to 250MB
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* 2. Media Type Selection (Auto detected with manual override) */}
          <div className="mb-3">
            <Form.Label className="small fw-semibold text-dark d-block">Media Category</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {[
                { type: 'video', label: '🎥 Video', icon: Film },
                { type: 'audio', label: '🎧 Audio', icon: Headphones },
                { type: 'image', label: '🖼️ Image', icon: ImageIcon },
                { type: 'document', label: '📄 Document', icon: FileText },
              ].map((m) => (
                <button
                  key={m.type}
                  type="button"
                  className={`btn btn-sm ${mediaType === m.type ? 'btn-success fw-bold' : 'btn-outline-secondary'}`}
                  onClick={() => setMediaType(m.type)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* 3. Title & Description */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold text-dark">
              Record Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Solemn Jubilee Inauguration Mass (2026)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold text-dark">Historical Context & Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Provide a historical narrative or descriptive summary for researchers..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          {/* 4. Event & Category */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Jubilee Event / Occasion</Form.Label>
                <Form.Select value={eventId} onChange={(e) => setEventId(e.target.value)}>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.title} ({ev.year})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Thematic Collection / Category</Form.Label>
                <Form.Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* 5. Year & Date */}
          <Row className="g-3 mb-3">
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Year</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="2026"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Exact Date (if recorded)</Form.Label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* 6. Speaker / Person & Source */}
          <Row className="g-3 mb-3">
            <Col xs={12} sm={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Speaker / Key Personality / Subject</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Fr. Provincial SJ, Fr. John Pujo SJ"
                  value={speaker}
                  onChange={(e) => {
                    setSpeaker(e.target.value);
                    setPerson(e.target.value);
                  }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-dark">Archival Shelf / Provenance</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Shembaganur Vault Room 2, Shelf 4"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* 7. Tags (Interactive tag pills) */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold text-dark">Keywords / Tags (Press Enter to add)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Add tag and hit Enter..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
            />
            <div className="d-flex flex-wrap gap-1 mt-2">
              {tags.map((t, idx) => (
                <span key={idx} className="badge bg-light text-dark border d-flex align-items-center gap-1">
                  #{t}
                  <X size={12} className="cursor-pointer text-danger" onClick={() => handleRemoveTag(t)} />
                </span>
              ))}
            </div>
          </Form.Group>

          {/* 8. Visibility & Featured Toggles */}
          <Row className="g-3 mb-4 p-3 bg-light rounded-3 border">
            <Col xs={6}>
              <Form.Check
                type="switch"
                id="featured-switch"
                label={<span className="small fw-semibold">Feature on Jubilee Dashboard</span>}
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
              />
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold mb-1">Access Visibility</Form.Label>
                <Form.Select
                  size="sm"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                >
                  <option value="public">Public (Open Heritage)</option>
                  <option value="restricted">Restricted (Archivists Only)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Upload Progress Indicator */}
          {uploading && (
            <div className="mb-4">
              <div className="d-flex justify-content-between small text-muted mb-1">
                <span>Uploading and hashing metadata...</span>
                <span>{progress}%</span>
              </div>
              <ProgressBar now={progress} variant="success" animated />
            </div>
          )}

          {/* Submit Button */}
          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" onClick={onHide} disabled={uploading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              className="fw-semibold px-4 shadow-sm"
              disabled={uploading}
            >
              {uploading ? 'Cataloguing...' : 'Save & Publish Record'}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
